import React, { useState, useEffect } from 'react';
import { IntegrationField } from '../types';
import { getWorkItemTypes } from '../../../api';
import HelpIcon from './HelpIcon';

// Define custom field types for Azure DevOps
type AzureCustomFieldType = 'defectWorkItemType' | 'iterationPath' | 'areaPath';

interface AzureCustomIntegrationFieldProps {
  field: IntegrationField;
  value: string;
  error?: string;
  integrationTypeId: number;
  customerIntegrationId: number | null;
  onChange: (name: string, value: string) => void;
  configValues: Record<string, any>;
}

const AzureCustomIntegrationField: React.FC<AzureCustomIntegrationFieldProps> = ({
  field,
  value,
  error,
  integrationTypeId,
  customerIntegrationId,
  onChange,
  configValues
}) => {
  const [workItemTypes, setWorkItemTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const commonLabelStyles = {
    display: 'block',
    marginBottom: 8,
    fontWeight: 500 as const
  };

  const commonSelectStyles = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 4,
    border: `1px solid ${error ? '#f44336' : '#ccc'}`,
    fontSize: 14,
    boxSizing: 'border-box' as const
  };

  const errorMessageStyles = {
    color: '#f44336',
    fontSize: 12,
    marginTop: 4
  };

  const fetchWorkItemTypes = async () => {
    if (!customerIntegrationId && (!configValues.organizationUrl || !configValues.projectName || !configValues.apiToken)) {
      // Don't try to fetch work item types if we don't have the necessary config values
      setLoadError('Please enter valid details for Organization, Project, and Personal Access API Token first');
      return;
    }

    if (workItemTypes.length > 0) {
      setLoading(false);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      // If we have a customerIntegrationId, use that to fetch work item types
      // Otherwise, use the current config values for validation
      const { workItemTypes: types } = await getWorkItemTypes(
        integrationTypeId,
        configValues.organizationUrl,
        configValues.projectName,
        configValues.apiToken
      );

      setWorkItemTypes(types);
    } catch (err) {
      console.error('Failed to load work item types:', err);
      setLoadError('Failed to load work item types. please check you have entered valid credentials above');
    } finally {
      setLoading(false);
    }
  };


  // Render the appropriate field based on the field name
  const renderCustomField = () => {
    switch (field.name as AzureCustomFieldType) {
      case 'defectWorkItemType':
        // If we have a value and not editing, show as label with change button
        if (value && !isEditing) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                ...commonSelectStyles,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                flex: 1
              }}>
                {value}
              </div>
              <button
                onClick={() => {
                  setIsEditing(true);
                  fetchWorkItemTypes();
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Change
              </button>
            </div>
          );
        }

        // If we have no work item types and we're not loading, show a button to fetch them
        if (workItemTypes.length === 0 && !loading && !value) {
          return (
            <div>
              <button
                onClick={fetchWorkItemTypes}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                Click to load work item types
              </button>
              {loadError && <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{loadError}</div>}
            </div>
          );
        }

        // Otherwise show the dropdown
        return (
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: loading || loadError ? '8px' : '0' }}>
              <select
                id={field.name}
                name={field.name}
                value={value}
                onChange={(e) => {
                  onChange(field.name, e.target.value);
                  setIsEditing(false);
                }}
                required={field.required}
                style={{ ...commonSelectStyles, flex: 1 }}
                disabled={loading}
              >
                <option value="">Select a work item type</option>
                {workItemTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  setIsEditing(false);
                  if (value) {
                    // If we had a previous value, restore it
                    onChange(field.name, value);
                  }
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
            {loading && <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>Loading work item types...</div>}
            {loadError && <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{loadError}</div>}
          </div>
        );

      default:
        // For any other custom fields, render a text input as fallback
        return (
          <input
            type="text"
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 4,
              border: `1px solid ${error ? '#f44336' : '#ccc'}`,
              fontSize: 14,
              boxSizing: 'border-box' as const
            }}
          />
        );
    }
  };

  // Render the field with common wrapper
  return (
    <div key={field.name} style={{ marginBottom: 16 }}>
      <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
        <span>
          {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
        </span>
        {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
      </label>
      {renderCustomField()}
      {error && <div style={errorMessageStyles}>{error}</div>}
    </div>
  );
};

export default AzureCustomIntegrationField;
